/* Wali Garage Manager — design tokens & components. Brand colours from the Wali Autofix logo. */
:root {
  --navy-900: #0a1f44;
  --navy-800: #102a5c;
  --navy-700: #1a3a7a;
  --navy-50: #eef2fa;
  --red-600: #d6232c;
  --red-50: #fdecec;
  --green-700: #0f7a4a;
  --green-50: #e8f6ef;
  --amber-700: #9a5b00;
  --amber-50: #fff5e0;
  --blue-700: #1f5fbf;
  --blue-50: #eaf1fd;
  --grey-900: #15191f;
  --grey-700: #3d4652;
  --grey-500: #6b7480;
  --grey-300: #cfd5dd;
  --grey-200: #e3e7ec;
  --grey-100: #f1f3f6;
  --grey-50: #f7f8fa;
  --white: #fff;

  --bg: var(--grey-50);
  --surface: var(--white);
  --text: var(--grey-900);
  --muted: var(--grey-500);
  --border: var(--grey-200);
  --primary: var(--navy-800);
  --primary-hover: var(--navy-700);
  --accent: var(--red-600);

  --radius: 10px;
  --radius-sm: 6px;
  --shadow: 0 1px 2px rgba(16, 24, 40, .06), 0 1px 3px rgba(16, 24, 40, .08);
  --shadow-lg: 0 12px 32px rgba(16, 24, 40, .18);
  --touch: 44px;
  --sidebar: 236px;
  --font: system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
  --mono: ui-monospace, "Cascadia Mono", "Segoe UI Mono", Consolas, monospace;
  color-scheme: light;
}

*, *::before, *::after { box-sizing: border-box; }
html, body { margin: 0; padding: 0; }
body { font-family: var(--font); font-size: 15px; line-height: 1.45; color: var(--text); background: var(--bg); -webkit-font-smoothing: antialiased; }
a { color: var(--blue-700); text-decoration: none; }
a:hover { text-decoration: underline; }
h1, h2, h3, h4 { margin: 0; line-height: 1.25; color: var(--grey-900); }
h1 { font-size: 22px; font-weight: 700; }
h2 { font-size: 17px; font-weight: 650; }
h3 { font-size: 15px; font-weight: 650; }
button, input, select, textarea { font: inherit; color: inherit; }
:focus-visible { outline: 3px solid #7aa7ff; outline-offset: 2px; }
.sr-only { position: absolute; width: 1px; height: 1px; overflow: hidden; clip: rect(0 0 0 0); white-space: nowrap; }

/* ───────── App shell ───────── */
.shell { display: grid; grid-template-columns: var(--sidebar) 1fr; min-height: 100vh; }
.sidebar { background: var(--navy-900); color: #dbe3f3; position: sticky; top: 0; height: 100vh; overflow-y: auto; display: flex; flex-direction: column; }
.sidebar .brand { padding: 16px 16px 12px; display: flex; align-items: center; gap: 10px; color: #fff; font-weight: 700; font-size: 16px; }
.sidebar .brand .mark { width: 34px; height: 34px; border-radius: 8px; background: var(--red-600); display: grid; place-items: center; font-weight: 800; }
.sidebar .brand small { display: block; font-weight: 400; color: #9fb0d0; font-size: 12px; }
.nav-group { padding: 8px 8px 4px; }
.nav-group-title { font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: #8195bd; padding: 8px 10px 4px; }
.nav-link { display: flex; align-items: center; gap: 10px; min-height: 40px; padding: 8px 10px; border-radius: 8px; color: #dbe3f3; font-weight: 500; }
.nav-link:hover { background: rgba(255, 255, 255, .07); text-decoration: none; }
.nav-link.active { background: rgba(255, 255, 255, .14); color: #fff; box-shadow: inset 3px 0 0 var(--red-600); }
.nav-link .count { margin-left: auto; background: var(--red-600); color: #fff; border-radius: 999px; font-size: 12px; padding: 0 7px; min-width: 22px; text-align: center; }
.sidebar .foot { margin-top: auto; padding: 12px 16px; font-size: 13px; color: #9fb0d0; border-top: 1px solid rgba(255, 255, 255, .08); }
.env-banner { background: var(--amber-50); color: var(--amber-700); border-bottom: 1px solid #f3d9a4; padding: 6px 16px; font-size: 13px; font-weight: 600; text-align: center; }

.main { min-width: 0; display: flex; flex-direction: column; }
.topbar { position: sticky; top: 0; z-index: 30; background: var(--surface); border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: 12px; padding: 10px 24px; }
.topbar .search { flex: 1; max-width: 520px; position: relative; }
.topbar .search input { width: 100%; height: 40px; border: 1px solid var(--border); border-radius: 999px; padding: 0 16px 0 38px; background: var(--grey-50); }
.topbar .search svg { position: absolute; left: 13px; top: 11px; color: var(--muted); }
.search-results { position: absolute; top: 46px; left: 0; right: 0; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow-lg); max-height: 60vh; overflow: auto; z-index: 40; }
.search-results a { display: block; padding: 10px 14px; color: var(--text); border-bottom: 1px solid var(--grey-100); }
.search-results a:hover, .search-results a:focus { background: var(--navy-50); text-decoration: none; }
.search-results .group { font-size: 11px; text-transform: uppercase; letter-spacing: .06em; color: var(--muted); padding: 8px 14px 4px; background: var(--grey-50); }
.menu-btn { display: none; }
.content { padding: 20px 24px 96px; max-width: 1280px; width: 100%; }

.mobile-nav { display: none; }

@media (max-width: 900px) {
  .shell { grid-template-columns: 1fr; }
  .sidebar { position: fixed; left: 0; top: 0; bottom: 0; width: 280px; z-index: 60; transform: translateX(-100%); transition: transform .2s ease; }
  .sidebar.open { transform: none; box-shadow: var(--shadow-lg); }
  .scrim { position: fixed; inset: 0; background: rgba(10, 20, 40, .4); z-index: 55; }
  .menu-btn { display: inline-flex; }
  .topbar { padding: 8px 12px; }
  .content { padding: 14px 12px 96px; }
  .mobile-nav { display: grid; grid-template-columns: repeat(5, 1fr); position: fixed; bottom: 0; left: 0; right: 0; background: var(--surface); border-top: 1px solid var(--border); z-index: 50; padding-bottom: env(safe-area-inset-bottom); }
  .mobile-nav a { display: flex; flex-direction: column; align-items: center; justify-content: center; min-height: 58px; font-size: 11px; color: var(--grey-700); gap: 2px; }
  .mobile-nav a.active { color: var(--primary); font-weight: 700; }
}

/* ───────── Page structure ───────── */
.page-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 12px; flex-wrap: wrap; margin-bottom: 16px; }
.page-head .sub { color: var(--muted); margin-top: 2px; font-size: 14px; }
.page-head .actions { display: flex; gap: 8px; flex-wrap: wrap; }
.back-link { display: inline-flex; align-items: center; gap: 4px; min-height: 32px; margin: -6px 0 4px -6px; padding: 4px 8px 4px 4px; border-radius: 8px; font-size: 14px; font-weight: 600; color: var(--primary); text-decoration: none; }
.back-link:hover { background: var(--navy-50); text-decoration: none; }
.back-link:focus-visible { outline: 2px solid var(--primary); outline-offset: 1px; }
@media (max-width: 720px) { .back-link { min-height: 40px; } }
@media print { .back-link { display: none; } }

.card { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); box-shadow: var(--shadow); }
.card-head { display: flex; align-items: center; justify-content: space-between; gap: 8px; padding: 14px 16px; border-bottom: 1px solid var(--border); flex-wrap: wrap; }
.card-body { padding: 16px; }
.card + .card { margin-top: 16px; }
.grid { display: grid; gap: 16px; }
.grid-2 { grid-template-columns: repeat(2, minmax(0, 1fr)); }
.grid-3 { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.grid-sidebar { grid-template-columns: minmax(0, 2fr) minmax(280px, 1fr); align-items: start; }
@media (max-width: 1000px) { .grid-sidebar { grid-template-columns: 1fr; } }
@media (max-width: 720px) { .grid-2, .grid-3 { grid-template-columns: 1fr; } }
.stack > * + * { margin-top: 12px; }
.row { display: flex; gap: 8px; align-items: center; flex-wrap: wrap; }
.spacer { flex: 1; }
.muted { color: var(--muted); }
.small { font-size: 13px; }
.mono { font-family: var(--mono); }
.right { text-align: right; }
.nowrap { white-space: nowrap; }
hr.sep { border: 0; border-top: 1px solid var(--border); margin: 16px 0; }

/* ───────── Dashboard tiles ───────── */
.tiles { display: grid; grid-template-columns: repeat(auto-fill, minmax(200px, 1fr)); gap: 12px; margin-bottom: 20px; }
.tile { display: block; background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); padding: 14px 16px; box-shadow: var(--shadow); color: var(--text); position: relative; min-height: 96px; }
.tile:hover { border-color: var(--navy-700); text-decoration: none; box-shadow: 0 0 0 3px var(--navy-50); }
.tile .label { font-size: 13px; color: var(--grey-700); font-weight: 600; }
.tile .value { font-size: 26px; font-weight: 750; margin-top: 4px; letter-spacing: -.01em; }
.tile .basis { font-size: 12px; color: var(--muted); margin-top: 4px; }
.tile.money .value { color: var(--navy-800); }
.tile.alert .value { color: var(--red-600); }

/* ───────── Buttons ───────── */
.btn { display: inline-flex; align-items: center; justify-content: center; gap: 6px; min-height: var(--touch); padding: 0 16px; border-radius: var(--radius-sm); border: 1px solid transparent; font-weight: 600; cursor: pointer; white-space: nowrap; text-decoration: none !important; transition: background .12s, border-color .12s; }
.btn-primary { background: var(--primary); color: #fff; }
.btn-primary:hover { background: var(--primary-hover); }
.btn-accent { background: var(--accent); color: #fff; }
.btn-accent:hover { background: #b81d25; }
.btn-secondary { background: var(--surface); color: var(--grey-900); border-color: var(--grey-300); }
.btn-secondary:hover { background: var(--grey-100); }
.btn-ghost { background: transparent; color: var(--grey-700); }
.btn-ghost:hover { background: var(--grey-100); }
.btn-danger { background: var(--surface); color: var(--red-600); border-color: #f1b8bb; }
.btn-danger:hover { background: var(--red-50); }
.btn-sm { min-height: 34px; padding: 0 10px; font-size: 14px; }
.btn[disabled] { opacity: .55; cursor: not-allowed; }
.btn .spin { width: 16px; height: 16px; border: 2px solid currentColor; border-right-color: transparent; border-radius: 50%; animation: spin .7s linear infinite; }
@keyframes spin { to { transform: rotate(360deg); } }
.icon-btn { min-width: var(--touch); padding: 0; }

/* ───────── Forms ───────── */
.form-grid { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 12px 16px; }
.form-grid .full { grid-column: 1 / -1; }
@media (max-width: 720px) { .form-grid { grid-template-columns: 1fr; } }
.field { display: flex; flex-direction: column; gap: 4px; }
.field label { font-size: 13px; font-weight: 600; color: var(--grey-700); }
.field .hint { font-size: 12px; color: var(--muted); }
.field .err { font-size: 13px; color: var(--red-600); }
.input, select.input, textarea.input { width: 100%; min-height: var(--touch); border: 1px solid var(--grey-300); border-radius: var(--radius-sm); padding: 8px 12px; background: var(--surface); }
textarea.input { min-height: 88px; resize: vertical; }
.input:focus { border-color: var(--navy-700); outline: 3px solid var(--navy-50); }
.input.invalid { border-color: var(--red-600); }
.input-prefix { position: relative; }
.input-prefix span { position: absolute; left: 12px; top: 50%; transform: translateY(-50%); color: var(--muted); }
.input-prefix .input { padding-left: 26px; }
.reg-input { font-family: var(--mono); font-weight: 700; letter-spacing: .08em; text-transform: uppercase; background: #ffd400 !important; border-color: #c9a800 !important; font-size: 18px; }
.check { display: flex; align-items: center; gap: 8px; min-height: var(--touch); }
.check input { width: 20px; height: 20px; }
.form-actions { display: flex; gap: 8px; justify-content: flex-end; margin-top: 16px; flex-wrap: wrap; align-items: center; }
.dirty-note { color: var(--amber-700); font-size: 13px; font-weight: 600; margin-right: auto; }

/* ───────── Tables ───────── */
.table-wrap { overflow-x: auto; }
table.table { width: 100%; border-collapse: collapse; font-size: 14px; }
.table th { text-align: left; font-size: 12px; text-transform: uppercase; letter-spacing: .04em; color: var(--muted); font-weight: 650; padding: 10px 12px; border-bottom: 1px solid var(--border); background: var(--grey-50); white-space: nowrap; }
.table td { padding: 11px 12px; border-bottom: 1px solid var(--grey-100); vertical-align: top; }
.table tr.clickable { cursor: pointer; }
.table tr.clickable:hover td { background: var(--navy-50); }
.table td.num, .table th.num { text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
.table tfoot td { font-weight: 700; border-top: 2px solid var(--border); }
@media (max-width: 720px) {
  .table.responsive thead { display: none; }
  .table.responsive tr { display: block; border-bottom: 1px solid var(--border); padding: 8px 0; }
  .table.responsive td { display: flex; justify-content: space-between; gap: 12px; border: 0; padding: 4px 12px; }
  .table.responsive td::before { content: attr(data-label); color: var(--muted); font-size: 12px; font-weight: 600; }
  .table.responsive td.num { text-align: right; }
}

/* ───────── Badges ───────── */
.badge { display: inline-flex; align-items: center; gap: 4px; border-radius: 999px; padding: 2px 9px; font-size: 12px; font-weight: 650; white-space: nowrap; background: var(--grey-100); color: var(--grey-700); }
.badge.blue { background: var(--blue-50); color: var(--blue-700); }
.badge.green { background: var(--green-50); color: var(--green-700); }
.badge.amber { background: var(--amber-50); color: var(--amber-700); }
.badge.red { background: var(--red-50); color: var(--red-600); }
.badge.navy { background: var(--navy-50); color: var(--navy-800); }
.reg-plate { display: inline-block; font-family: var(--mono); font-weight: 800; letter-spacing: .06em; background: #ffd400; color: #111; border: 1px solid #c9a800; border-radius: 4px; padding: 1px 7px; font-size: 13px; white-space: nowrap; }

/* ───────── Feedback ───────── */
.alert { border-radius: var(--radius-sm); padding: 12px 14px; border: 1px solid; font-size: 14px; }
.alert.error { background: var(--red-50); border-color: #f1b8bb; color: #8c1c22; }
.alert.warn { background: var(--amber-50); border-color: #f3d9a4; color: #6d4100; }
.alert.info { background: var(--blue-50); border-color: #c6d8f7; color: #173f80; }
.alert.success { background: var(--green-50); border-color: #b6e0ca; color: #0b5434; }
.alert strong { display: block; margin-bottom: 2px; }
.empty { text-align: center; padding: 40px 16px; color: var(--muted); }
.empty h3 { color: var(--grey-700); margin-bottom: 4px; }
.loading { display: flex; align-items: center; gap: 10px; color: var(--muted); padding: 24px 16px; }
.skeleton { background: linear-gradient(90deg, var(--grey-100), var(--grey-200), var(--grey-100)); background-size: 200% 100%; animation: shimmer 1.2s infinite; border-radius: 6px; height: 14px; }
@keyframes shimmer { to { background-position: -200% 0; } }

.toasts { position: fixed; right: 16px; bottom: 16px; z-index: 100; display: flex; flex-direction: column; gap: 8px; max-width: min(420px, calc(100vw - 32px)); }
@media (max-width: 900px) { .toasts { bottom: 76px; } }
.toast { background: var(--grey-900); color: #fff; border-radius: var(--radius-sm); padding: 12px 14px; box-shadow: var(--shadow-lg); font-size: 14px; display: flex; gap: 10px; align-items: flex-start; }
.toast.error { background: #8c1c22; }
.toast.success { background: #0b5434; }
.toast button { background: none; border: 0; color: inherit; margin-left: auto; cursor: pointer; font-size: 18px; line-height: 1; }

/* ───────── Modal ───────── */
.modal-backdrop { position: fixed; inset: 0; background: rgba(10, 20, 40, .45); z-index: 80; display: grid; place-items: center; padding: 16px; }
.modal { background: var(--surface); border-radius: 12px; box-shadow: var(--shadow-lg); width: 100%; max-width: 560px; max-height: calc(100vh - 32px); display: flex; flex-direction: column; }
.modal.wide { max-width: 860px; }
.modal-head { padding: 16px 18px; border-bottom: 1px solid var(--border); display: flex; align-items: center; gap: 8px; }
.modal-body { padding: 18px; overflow-y: auto; }
.modal-foot { padding: 12px 18px; border-top: 1px solid var(--border); display: flex; justify-content: flex-end; gap: 8px; flex-wrap: wrap; }
@media (max-width: 720px) { .modal-backdrop { place-items: end center; padding: 0; } .modal { border-radius: 14px 14px 0 0; max-height: 92vh; } }

/* ───────── Tabs & filters ───────── */
.tabs { display: flex; gap: 4px; border-bottom: 1px solid var(--border); margin-bottom: 16px; overflow-x: auto; }
.tab { padding: 10px 14px; min-height: var(--touch); border: 0; background: none; border-bottom: 3px solid transparent; font-weight: 600; color: var(--grey-700); cursor: pointer; white-space: nowrap; }
.tab.active { color: var(--primary); border-bottom-color: var(--accent); }
.filters { display: flex; gap: 8px; flex-wrap: wrap; align-items: center; margin-bottom: 12px; }
.chip { min-height: 36px; padding: 0 12px; border-radius: 999px; border: 1px solid var(--grey-300); background: var(--surface); font-weight: 600; font-size: 14px; cursor: pointer; }
.chip.active { background: var(--navy-800); border-color: var(--navy-800); color: #fff; }

/* ───────── Detail lists ───────── */
dl.kv { display: grid; grid-template-columns: max-content 1fr; gap: 6px 16px; margin: 0; font-size: 14px; }
dl.kv dt { color: var(--muted); }
dl.kv dd { margin: 0; overflow-wrap: anywhere; }
.timeline { list-style: none; margin: 0; padding: 0; }
.timeline li { padding: 8px 0 8px 18px; border-left: 2px solid var(--grey-200); position: relative; font-size: 14px; }
.timeline li::before { content: ""; position: absolute; left: -6px; top: 13px; width: 10px; height: 10px; background: var(--navy-700); border-radius: 50%; }

/* ───────── Vehicle confirmation card ───────── */
.vehicle-card { display: grid; grid-template-columns: 160px 1fr; gap: 16px; border: 1px solid var(--border); border-radius: var(--radius); padding: 16px; background: var(--surface); }
.vehicle-card .img { aspect-ratio: 4 / 3; border-radius: 8px; background: var(--grey-100); display: grid; place-items: center; color: var(--muted); font-size: 12px; text-align: center; overflow: hidden; position: relative; }
.vehicle-card .img img { width: 100%; height: 100%; object-fit: cover; }
.vehicle-card .img .label { position: absolute; bottom: 4px; left: 4px; right: 4px; background: rgba(0, 0, 0, .6); color: #fff; font-size: 11px; border-radius: 4px; padding: 2px; }
.vehicle-card .title { font-size: 18px; font-weight: 700; }
.vehicle-card .specs { display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 8px; margin-top: 10px; }
.vehicle-card .spec { background: var(--grey-50); border-radius: 6px; padding: 6px 8px; }
.vehicle-card .spec small { display: block; color: var(--muted); font-size: 11px; text-transform: uppercase; letter-spacing: .04em; }
@media (max-width: 560px) { .vehicle-card { grid-template-columns: 1fr; } }

/* ───────── Calendar ───────── */
.week { display: grid; grid-template-columns: repeat(6, minmax(150px, 1fr)); gap: 8px; overflow-x: auto; }
.day { background: var(--surface); border: 1px solid var(--border); border-radius: var(--radius); min-height: 220px; display: flex; flex-direction: column; }
.day.today { border-color: var(--navy-700); box-shadow: 0 0 0 2px var(--navy-50); }
.day-head { padding: 8px 10px; border-bottom: 1px solid var(--border); font-weight: 700; font-size: 14px; }
.day-head small { display: block; font-weight: 500; color: var(--muted); }
.cap { height: 6px; background: var(--grey-100); border-radius: 3px; overflow: hidden; margin-top: 4px; }
.cap span { display: block; height: 100%; background: var(--green-700); }
.cap.full span { background: var(--red-600); }
.cap.high span { background: var(--amber-700); }
.booking { margin: 6px; padding: 6px 8px; border-radius: 6px; border-left: 4px solid var(--navy-700); background: var(--navy-50); font-size: 13px; cursor: pointer; }
.booking.bodywork { border-left-color: var(--red-600); background: var(--red-50); }
.booking.requested { border-style: dashed; border-left-style: solid; opacity: .85; outline: 1px dashed var(--grey-300); }
@media (max-width: 900px) { .week { grid-template-columns: 1fr; } .day { min-height: 0; } }

/* ───────── Print (invoices/quotes) ───────── */
.doc { background: #fff; max-width: 820px; margin: 0 auto; padding: 32px; border: 1px solid var(--border); border-radius: var(--radius); }
.doc-head { display: flex; justify-content: space-between; gap: 24px; align-items: flex-start; border-bottom: 3px solid var(--navy-800); padding-bottom: 16px; }
.doc-head img { max-width: 300px; height: auto; }
.doc-title { font-size: 28px; font-weight: 800; color: var(--navy-800); text-align: right; }
.doc table { width: 100%; border-collapse: collapse; margin-top: 16px; font-size: 14px; }
.doc th { background: var(--navy-800); color: #fff; text-align: left; padding: 8px; font-size: 12px; text-transform: uppercase; }
.doc td { padding: 8px; border-bottom: 1px solid var(--grey-200); }
.doc .totals { margin-left: auto; width: 280px; margin-top: 12px; }
.doc .totals div { display: flex; justify-content: space-between; padding: 4px 0; }
.doc .totals .grand { font-size: 18px; font-weight: 800; border-top: 2px solid var(--navy-800); padding-top: 8px; }
.doc-vehicle { display: flex; flex-wrap: wrap; align-items: baseline; gap: 6px 12px; margin-top: 10px; padding-top: 8px; border-top: 1px solid var(--grey-200); }
.doc-reg { font-size: 18px; letter-spacing: .06em; border: 2px solid var(--navy-800); border-radius: 4px; padding: 1px 8px; }
.doc-reg-missing { font-weight: 600; color: var(--grey-700); }
.doc-foot { margin-top: 24px; font-size: 12px; color: var(--grey-700); border-top: 1px solid var(--grey-200); padding-top: 12px; }
.review-footer { display: flex; align-items: center; gap: 16px; margin-top: 16px; padding-top: 12px; border-top: 1px solid var(--grey-200); break-inside: avoid; page-break-inside: avoid; }
.review-footer .qr-code { width: 32mm; height: 32mm; flex: none; background: #fff; }
.review-footer strong { display: block; font-size: 14px; color: #000; }
.review-footer div div { font-size: 13px; color: #000; }
.qr-code { display: block; width: 100%; height: auto; background: #fff; }
.paid-stamp { display: inline-block; border: 3px solid var(--green-700); color: var(--green-700); font-weight: 800; padding: 4px 12px; border-radius: 6px; transform: rotate(-4deg); letter-spacing: .1em; }
@media print {
  @page { size: A4; margin: 12mm; }
  body { background: #fff; }
  .sidebar, .topbar, .mobile-nav, .no-print, .toasts, .env-banner { display: none !important; }
  .shell { display: block; }
  .content { padding: 0; max-width: none; }
  .doc { border: 0; padding: 0; max-width: none; }
}

.update-bar { position: sticky; top: 0; z-index: 30; display: flex; flex-wrap: wrap; align-items: center; justify-content: space-between; gap: 8px 16px; padding: 10px 16px; background: var(--blue-50); border-bottom: 1px solid var(--blue-700); color: var(--navy-800); font-weight: 600; }
.btn-delete { color: var(--red-600); }
